// The live draft room's socket, for a screen (REALTIME.md, BACKLOG D10).
//
// THE APP'S ONE WEBSOCKET, and it is not `fetch`, so it lives here rather than
// in `api.ts`. Same origin as the page — `/api/live/draft/:id`, or a league
// mock's `/api/live/mock/:id/:mockId` (MOCK_DRAFT.md §6.2) — which is the
// whole reason the session cookie rides on it (the A11 decision).
//
// What it keeps: the view, built from the room's snapshot and then from each
// event by assignment (`applyDraftMessage`); the last `seq`, so a gap asks the
// room to replay; and how far this device's clock is from the room's, so a
// countdown shows the room's deadline and not this device's guess. A dropped
// socket comes back on its own, sooner when the page is shown again, and the
// room sends the whole view on the way in — except after the last pick, and
// from a league mock's room that closed, when there is nothing to come back to.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DraftServerMessage,
  type DraftClientMessage,
  type DraftOfferClosed,
  type DraftView,
  type ErrorCode,
} from '@fsp/domain';
import { applyDraftMessage } from './draftApply';

export type RoomConnection =
  | 'connecting'
  | 'live'
  | 'reconnecting'
  | 'offline'
  | 'signed-out'
  /** No longer in the league: removed, or left, while the room was open (4004). */
  | 'removed'
  /** A league mock's room that closed: nobody started it (4002), or it finished an hour ago (4003). */
  | 'closed';

export interface RoomRefusal {
  intentId: string;
  code: ErrorCode;
  message: string;
}

export interface LiveDraft {
  view: DraftView | null;
  connection: RoomConnection;
  /** The room's clock minus this device's, in milliseconds. */
  offset: number;
  /** Send an intent; false when there is no open socket to send it on. */
  send: (message: DraftClientMessage) => boolean;
  /**
   * My offers that last stopped waiting, and why, numbered so the same batch
   * is told once (DRAFT_ROOM.md "Trading during the draft").
   */
  closedOffers: OfferNews;
}

/** Offers that stopped waiting, the latest batch. */
export interface OfferNews {
  n: number;
  closed: DraftOfferClosed[];
}

/** Waits between tries after a drop: quick first, then every ten seconds. */
const BACKOFF_MS = [500, 1_000, 2_000, 4_000, 8_000, 10_000];
/** How often the clock offset is measured again (REALTIME.md "Clocks"). */
const PING_MS = 30_000;

/**
 * A fresh id for an intent, so a retry after a drop is the same intent.
 * `getRandomValues` rather than `randomUUID`, which a page on a plain-http
 * address (a phone on the dev machine's LAN) does not have.
 */
export function intentId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  return `i-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

function socketUrl(leagueId: string, mockId: string | null, watch: boolean): string {
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const path =
    mockId === null
      ? `/api/live/draft/${encodeURIComponent(leagueId)}${watch ? '?watch=1' : ''}`
      : `/api/live/mock/${encodeURIComponent(leagueId)}/${encodeURIComponent(mockId)}`;
  return `${scheme}://${window.location.host}${path}`;
}

export function useLiveDraft(
  leagueId: string | null,
  onRefused: (refusal: RoomRefusal) => void,
  /**
   * This device's clock, handed in: the gateway reads no wall clock of its own
   * (the lint's rule, ICD-U01 invariant 5), and the offset is measured against it.
   */
  deviceNow: () => number,
  /** A league mock's room in the league, rather than the league's own. */
  mockId: string | null = null,
  /**
   * For a league mock: whether it is open still. A socket the room refused
   * cannot say why, so before each try again the page asks, and stops for
   * good once the mock has closed or finished.
   */
  stillOpen: (() => Promise<boolean>) | null = null,
  /**
   * A screen that shows the room and does nothing in it, the draft on a TV
   * (M3, B-608): the room counts it as nobody's presence and takes no intent
   * from it.
   */
  watch = false,
): LiveDraft {
  const [view, setView] = useState<DraftView | null>(null);
  const [connection, setConnection] = useState<RoomConnection>('connecting');
  const [offset, setOffset] = useState(0);
  const [closedOffers, setClosedOffers] = useState<OfferNews>({ n: 0, closed: [] });
  const ws = useRef<WebSocket | null>(null);
  const seq = useRef(0);
  const refused = useRef(onRefused);
  refused.current = onRefused;
  const clockRef = useRef(deviceNow);
  clockRef.current = deviceNow;
  const over = useRef(false);
  const asking = useRef(stillOpen);
  asking.current = stillOpen;

  useEffect(() => {
    if (leagueId === null) return;
    let stopped = false;
    /** The room is gone or final: nothing brings the socket back, not even the page shown again. */
    let ended = false;
    let tries = 0;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let ping: ReturnType<typeof setInterval> | null = null;

    const open = () => {
      if (stopped) return;
      const socket = new WebSocket(socketUrl(leagueId, mockId, watch));
      ws.current = socket;
      socket.onopen = () => {
        tries = 0;
        ping = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'ping', payload: { sentAt: clockRef.current() } }));
          }
        }, PING_MS);
      };
      socket.onmessage = (event) => {
        let raw: unknown;
        try {
          raw = JSON.parse(String(event.data));
        } catch {
          raw = null;
        }
        const parsed = DraftServerMessage.safeParse(raw);
        if (!parsed.success) {
          // FAIL LOUDLY: a message this client cannot read is a contract that
          // drifted, and the room sends the whole view on the next connect.
          console.error('[draft] a message from the room did not parse:', parsed.error.message);
          return;
        }
        const m = parsed.data;
        if (m.type === 'snapshot') {
          seq.current = m.seq;
          setOffset(Date.parse(m.at) - clockRef.current());
          setConnection('live');
        } else if (m.type === 'pong') {
          const now = clockRef.current();
          setOffset(Date.parse(m.at) + (now - m.payload.sentAt) / 2 - now);
          return;
        } else if (m.type === 'intent.rejected') {
          refused.current({
            intentId: m.payload.intentId,
            code: m.payload.code,
            message: m.payload.message,
          });
          return;
        } else if (m.seq > seq.current + 1) {
          // A GAP: something was missed. Apply this one — assignment is safe to
          // repeat — and ask for what came between.
          socket.send(JSON.stringify({ type: 'resync', payload: { since: seq.current } }));
        }
        if (m.type !== 'snapshot') seq.current = Math.max(seq.current, m.seq);
        if (m.type === 'draft.offers' && m.payload.closed.length > 0) {
          const closed = m.payload.closed;
          setClosedOffers((was) => ({ n: was.n + 1, closed }));
        }
        setView((v) => applyDraftMessage(v, m));
      };
      socket.onclose = (event) => {
        if (ping) clearInterval(ping);
        ping = null;
        if (stopped) return;
        if (event.code === 4001) {
          setConnection('signed-out');
          return;
        }
        if (event.code === 4004) {
          ended = true;
          setConnection('removed');
          return;
        }
        // NOTHING TO COME BACK TO: the board is final once every pick is in,
        // and a league mock's room that closed is gone.
        const end = () => {
          ended = true;
          setConnection('closed');
        };
        if (event.code === 4002 || event.code === 4003 || over.current) {
          end();
          return;
        }
        tries += 1;
        setConnection(tries > 3 ? 'offline' : 'reconnecting');
        const wait = BACKOFF_MS[Math.min(tries - 1, BACKOFF_MS.length - 1)]!;
        const ask = asking.current;
        if (ask === null) {
          retry = setTimeout(open, wait);
          return;
        }
        // A LEAGUE MOCK ASKS FIRST: one that finished or closed while this
        // socket was away is over, not unreachable. An answer that does not
        // come is no answer, and the page keeps trying.
        void ask()
          .catch(() => true)
          .then((open_) => {
            if (stopped) return;
            if (!open_) end();
            else retry = setTimeout(open, wait);
          });
      };
    };

    // BACK SOONER WHEN THE PAGE IS SHOWN AGAIN: a phone that slept dropped its
    // socket, and the person looking at it now wants the room now.
    const shown = () => {
      if (document.visibilityState !== 'visible' || ended) return;
      const s = ws.current;
      if (s === null || s.readyState === WebSocket.CLOSED) {
        if (retry) clearTimeout(retry);
        tries = 0;
        open();
      }
    };
    document.addEventListener('visibilitychange', shown);
    open();
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', shown);
      if (retry) clearTimeout(retry);
      if (ping) clearInterval(ping);
      ws.current?.close(1000, 'left the room');
      ws.current = null;
    };
  }, [leagueId, mockId, watch]);

  useEffect(() => {
    over.current = view?.status === 'complete';
  }, [view]);

  const send = useCallback((message: DraftClientMessage): boolean => {
    const socket = ws.current;
    if (socket === null || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify(message));
    return true;
  }, []);

  return { view, connection, offset, send, closedOffers };
}
